import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, apiErrorMessage } from '../lib/api';

interface ChromeDiagnostics {
  repo_cache_dir: string;
  repo_cache_chrome_path: string | null;
  explicit_env_path: string | null;
  resolved: {
    source: 'env' | 'repo-cache' | 'system' | 'puppeteer-default';
    path: string | null;
  };
  searched_paths: string[];
}

interface PdfTestResult {
  ok: boolean;
  size_bytes?: number;
  error?: { name: string; message: string };
  resolution?: ChromeDiagnostics['resolved'];
}

interface InstallResult {
  ok: boolean;
  build_id?: string;
  platform?: string;
  binary_path?: string;
  cache_dir: string;
  error?: { name: string; message: string; stack?: string | null };
  duration_ms: number;
}

const SOURCE_LABEL: Record<ChromeDiagnostics['resolved']['source'], string> = {
  env: 'PUPPETEER_EXECUTABLE_PATH (.env)',
  'repo-cache': 'Repo-lokaler Cache (server/.puppeteer-cache)',
  system: 'System-installierter Browser',
  'puppeteer-default': 'Kein Browser gefunden',
};

export function DiagnosticsCard() {
  const chromeQ = useQuery({
    queryKey: ['admin', 'diagnostics', 'chrome'],
    queryFn: async () => {
      const { data } = await api.get<ChromeDiagnostics>('/admin/diagnostics/chrome');
      return data;
    },
    staleTime: 30_000,
  });

  const [testResult, setTestResult] = useState<PdfTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [showPaths, setShowPaths] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installResult, setInstallResult] = useState<InstallResult | null>(null);

  const runPdfTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post<PdfTestResult>('/admin/diagnostics/pdf');
      setTestResult(data);
    } catch (err) {
      const e = err as { response?: { data?: PdfTestResult } };
      if (e?.response?.data) {
        setTestResult(e.response.data);
      } else {
        setTestResult({ ok: false, error: { name: 'NetworkError', message: apiErrorMessage(err) } });
      }
    } finally {
      setTesting(false);
    }
  };

  const runInstallChrome = async () => {
    if (!window.confirm('Chrome wird in den repo-Cache geladen — ca. 150 MB Download. Fortfahren?')) return;
    setInstalling(true);
    setInstallResult(null);
    setTestResult(null);
    try {
      const { data } = await api.post<InstallResult>('/admin/diagnostics/install-chrome', null, {
        timeout: 10 * 60 * 1000, // 10 Minuten — Download kann dauern
      });
      setInstallResult(data);
      chromeQ.refetch();
    } catch (err) {
      const e = err as { response?: { data?: InstallResult } };
      if (e?.response?.data) {
        setInstallResult(e.response.data);
      } else {
        setInstallResult({
          ok: false,
          cache_dir: '',
          duration_ms: 0,
          error: { name: 'NetworkError', message: apiErrorMessage(err) },
        });
      }
    } finally {
      setInstalling(false);
    }
  };

  if (chromeQ.isLoading) return null;
  if (chromeQ.error) return null;
  const data = chromeQ.data!;
  const found = data.resolved.source !== 'puppeteer-default';
  const isEdge =
    !!data.resolved.path &&
    /msedge\.exe|microsoft.?edge/i.test(data.resolved.path);
  const isEnvSource = data.resolved.source === 'env';
  const repoCacheAvailable = !!data.repo_cache_chrome_path;
  const recommendSwitchToCache = isEnvSource && isEdge && repoCacheAvailable;
  const testFailed = testResult && testResult.ok === false;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-title">PDF-Export-Diagnose</div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <span
          style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: 12,
            background: found ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
            color: found ? 'var(--color-success-text)' : 'var(--color-danger-text)',
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {found ? '✓ Chrome gefunden' : '✗ Chrome nicht gefunden'}
        </span>
        <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          Quelle: <strong>{SOURCE_LABEL[data.resolved.source]}</strong>
        </span>
      </div>

      {data.resolved.path && (
        <div style={{ fontSize: 12.5, color: 'var(--color-text-strong)', marginBottom: 12, wordBreak: 'break-all' }}>
          <span style={{ color: 'var(--color-text-soft)', fontWeight: 600 }}>Binary: </span>
          <code style={{ fontFamily: 'monospace', background: 'var(--color-surface-alt)', padding: '2px 6px', borderRadius: 4 }}>
            {data.resolved.path}
          </code>
        </div>
      )}

      <div style={{ fontSize: 12.5, color: 'var(--color-text-strong)', marginBottom: 14, wordBreak: 'break-all' }}>
        <span style={{ color: 'var(--color-text-soft)', fontWeight: 600 }}>Cache-Verzeichnis: </span>
        <code style={{ fontFamily: 'monospace', background: 'var(--color-surface-alt)', padding: '2px 6px', borderRadius: 4 }}>
          {data.repo_cache_dir}
        </code>
      </div>

      {!found && (
        <div
          style={{
            background: 'var(--color-danger-soft)',
            borderLeft: '3px solid var(--color-danger-strong)',
            padding: '10px 14px',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--color-text-strong)',
            marginBottom: 12,
          }}
        >
          <strong>So fixen:</strong>
          <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
            <li>
              In <code>D:\Stromo\server</code> als der Service-User <code>npm install</code> ausführen — der postinstall lädt Chrome in den repo-Cache.
            </li>
            <li>
              Alternativ: <code>PUPPETEER_EXECUTABLE_PATH=...</code> in <code>server/.env</code> setzen (Pfad zu chrome.exe).
            </li>
            <li>
              Service neustarten: <code>nssm restart Stromo</code>.
            </li>
          </ol>
        </div>
      )}

      {(recommendSwitchToCache || (testFailed && isEdge)) && (
        <div
          style={{
            background: '#fffaf0',
            borderLeft: '3px solid #f0b94a',
            padding: '10px 14px',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--color-text-strong)',
            marginBottom: 12,
          }}
        >
          <strong>⚠ Microsoft Edge ist mit Puppeteer 24 unzuverlässig</strong>
          <div style={{ marginTop: 6 }}>
            {repoCacheAvailable ? (
              <>
                Chrome ist im repo-Cache vorhanden (
                <code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {data.repo_cache_chrome_path}
                </code>
                ). Empfohlene Lösung:
                <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  <li>
                    In <code>D:\Stromo\server\.env</code> die Zeile{' '}
                    <code>PUPPETEER_EXECUTABLE_PATH=...</code> leeren oder löschen
                  </li>
                  <li>
                    <code>nssm restart Stromo</code>
                  </li>
                  <li>Hier auf „Neu prüfen" klicken</li>
                </ol>
              </>
            ) : (
              <>
                Chrome ist <strong>nicht</strong> im repo-Cache. Manuell installieren:
                <pre
                  style={{
                    background: 'var(--color-surface-alt)',
                    padding: 8,
                    borderRadius: 4,
                    fontSize: 11.5,
                    marginTop: 6,
                    overflowX: 'auto',
                  }}
                >
{`cd D:\\Stromo\\server
node scripts\\install-puppeteer-chrome.mjs
:: Dann PUPPETEER_EXECUTABLE_PATH aus .env entfernen
nssm restart Stromo`}
                </pre>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={runPdfTest}
          disabled={testing || installing}
        >
          {testing ? 'Teste …' : 'Test-PDF erzeugen'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={runInstallChrome}
          disabled={installing || testing}
        >
          {installing ? 'Installiere Chrome … (~150 MB, mehrere Minuten)' : 'Chrome jetzt installieren'}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => chromeQ.refetch()}
          disabled={installing}
        >
          Neu prüfen
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setShowPaths((s) => !s)}
        >
          {showPaths ? 'Pfade ausblenden' : `Geprüfte Pfade zeigen (${data.searched_paths.length})`}
        </button>
      </div>

      {installResult && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 6,
            background: installResult.ok ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
            color: installResult.ok ? 'var(--color-success-text)' : 'var(--color-danger-text)',
            fontSize: 13,
          }}
        >
          {installResult.ok ? (
            <>
              <strong>✓ Chrome installiert</strong> (Build {installResult.build_id},{' '}
              {installResult.platform}, {(installResult.duration_ms / 1000).toFixed(1)}s)
              <div style={{ fontSize: 11.5, marginTop: 4, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {installResult.binary_path}
              </div>
            </>
          ) : (
            <>
              <strong>✗ Chrome-Install fehlgeschlagen</strong>:{' '}
              <span style={{ fontWeight: 600 }}>{installResult.error?.name}</span> —{' '}
              {installResult.error?.message}
              {installResult.error?.stack && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Stack-Trace</summary>
                  <pre
                    style={{
                      marginTop: 6,
                      fontSize: 10.5,
                      lineHeight: 1.4,
                      background: 'rgba(0,0,0,.06)',
                      padding: 8,
                      borderRadius: 4,
                      maxHeight: 240,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: 'var(--color-text-strong)',
                    }}
                  >
                    {installResult.error.stack}
                  </pre>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {testResult && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 6,
            background: testResult.ok ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
            color: testResult.ok ? 'var(--color-success-text)' : 'var(--color-danger-text)',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {testResult.ok ? (
            <span>✓ PDF-Test erfolgreich — {testResult.size_bytes} Bytes erzeugt.</span>
          ) : (
            <span>
              ✗ PDF-Test fehlgeschlagen: <strong>{testResult.error?.name}</strong> —{' '}
              {testResult.error?.message}
            </span>
          )}
        </div>
      )}

      {showPaths && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: 'var(--color-surface-alt)',
            borderRadius: 6,
            fontSize: 11.5,
            fontFamily: 'monospace',
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {data.searched_paths.map((p, i) => (
            <div key={i} style={{ padding: '2px 0', color: 'var(--color-text-muted)' }}>
              {p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
